import type { ThemeAIRun } from '../types/gotypes'

export interface ThemeAIRunProgressView {
  /** 0-100, or undefined while nothing is known yet (indeterminate bar). */
  percent?: number
  /** What the run is doing now, e.g. "Adding sentiment and emotion". */
  phase?: string
  /** The phase's own count, e.g. "1 of 3 batches". */
  detail?: string
}

const PHASE_LABEL: Record<string, string> = {
  extracting: 'Reading the responses and matching themes',
  classifying: 'Adding sentiment and emotion',
  landing: 'Saving the results',
}

// Where each phase starts and ends on the one bar. A run without sentiment or
// emotion has no classifying phase, so reading the responses takes its share.
const spans = (looped: boolean): Record<string, [number, number]> =>
  looped
    ? { extracting: [0, 60], classifying: [60, 95], landing: [95, 100] }
    : { extracting: [0, 95], landing: [95, 100] }

// One bar for the whole run that only moves forward (Adam, 23 Sep: it "goes
// up to 33% then down again" because each phase restarted its own batch
// count). Each phase fills its own span of the bar; results landing count
// as the last stretch.
export const themeAIRunProgress = (run?: ThemeAIRun): ThemeAIRunProgressView => {
  if (!run) return {}
  const total = run.records_total ?? 0
  const settled = run.records_settled ?? 0
  if (total > 0 && settled > 0) {
    return { percent: Math.min(100, Math.round((settled / total) * 100)), detail: `${settled.toLocaleString()} of ${total.toLocaleString()} responses` }
  }
  const phase = run.phase || ''
  const span = spans(Boolean(run.include_sentiment || run.include_emotion))[phase]
  if (!span) return {}
  const [start, end] = span
  const requestsTotal = run.requests_total ?? 0
  const requestsDone = run.requests_done ?? 0
  const within = requestsTotal > 0 ? Math.min(1, requestsDone / requestsTotal) : 0
  return {
    percent: Math.round(start + (end - start) * within),
    phase: PHASE_LABEL[phase],
    detail: requestsTotal > 0 ? `${requestsDone.toLocaleString()} of ${requestsTotal.toLocaleString()} batches` : undefined,
  }
}
